import React, { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Square, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceRecorder({ onTranscription, disabled }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [webSpeechText, setWebSpeechText] = useState("");
  const [useWhisper, setUseWhisper] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);

  const webSpeechAvailable =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
    };
  }, []);

  const startWebSpeech = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalText = "";
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalText += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setWebSpeechText(finalText + interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        console.error("Web Speech error:", event.error);
      }
    };

    recognition.onend = () => {
      if (recording) {
        try { recognition.start(); } catch {}
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const startRecording = async () => {
    setDuration(0);
    setWebSpeechText("");
    chunksRef.current = [];

    // Always start MediaRecorder for Whisper fallback
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4",
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
    } catch (err) {
      toast.error("Microphone access denied");
      return;
    }

    // Start Web Speech API if available
    if (webSpeechAvailable && !useWhisper) {
      startWebSpeech();
    }

    setRecording(true);
    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
  };

  const stopRecording = async () => {
    setRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop Web Speech
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }

    // Stop MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
    }

    // Use Web Speech result if available and not forced Whisper
    if (webSpeechText.trim() && !useWhisper) {
      onTranscription(webSpeechText.trim(), "web_speech");
      setWebSpeechText("");
      return;
    }

    // Fallback to Whisper
    if (chunksRef.current.length > 0) {
      setTranscribing(true);
      try {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, "recording.webm");
        const res = await axios.post(`${BACKEND_URL}/api/transcribe`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data.text) {
          onTranscription(res.data.text, "whisper");
        } else {
          toast.error("No speech detected");
        }
      } catch {
        toast.error("Whisper transcription failed");
      } finally {
        setTranscribing(false);
      }
    }
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="flex items-center gap-2" data-testid="voice-recorder">
      {transcribing ? (
        <Button disabled variant="outline" size="sm" className="rounded-sm gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing...
        </Button>
      ) : recording ? (
        <>
          <Button
            data-testid="stop-recording-btn"
            onClick={stopRecording}
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm gap-1.5"
          >
            <Square className="w-3 h-3" /> Stop ({formatDuration(duration)})
          </Button>
          {webSpeechText && (
            <span className="text-xs text-slate-500 truncate max-w-[200px]">
              {webSpeechText.slice(-60)}...
            </span>
          )}
        </>
      ) : (
        <>
          <Button
            data-testid="start-recording-btn"
            onClick={startRecording}
            disabled={disabled}
            variant="outline"
            size="sm"
            className="rounded-sm gap-1.5 border-[#003057] text-[#003057] hover:bg-[#003057]/5"
          >
            <Mic className="w-3.5 h-3.5" />
            {webSpeechAvailable ? "Voice" : "Whisper"}
          </Button>
          <button
            onClick={() => setUseWhisper(!useWhisper)}
            data-testid="toggle-whisper-btn"
            className={`text-[10px] px-2 py-1 rounded-full transition-colors ${
              useWhisper
                ? "bg-[#003057] text-white"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
            title={useWhisper ? "Using OpenAI Whisper" : "Using browser speech (click for Whisper)"}
          >
            {useWhisper ? "Whisper" : "Browser"}
          </button>
        </>
      )}
    </div>
  );
}
